import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Popup } from '@progress/kendo-react-popup';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { Button } from '@ui';

function ActionsCell({ colSpan, className, ariaColumnIndex, style, actionsList, children }) {
  const anchor = useRef(null);
  const popup = useRef(null);
  const [open, setOpen] = useState(false);

  const onActionMenuClick = useCallback(() => {
    setOpen(!open);
  }, [open]);

  const handleBodyClick = useCallback((event) => {
    if (popup.current?._popup?.contains(event.target)) return;
    if (anchor.current.contains(event.target)) return;

    setOpen(false);
  }, []);

  useEffect(() => {
    document.body.addEventListener('click', handleBodyClick);
    return () => {
      document.body.removeEventListener('click', handleBodyClick);
    };
  }, []);

  return (
    <td
      colSpan={colSpan}
      className={className}
      role="gridcell"
      aria-colindex={ariaColumnIndex}
      aria-selected="false"
      style={style}>
      {children}
      <Button ref={anchor} type="button" onClick={onActionMenuClick}>
        <i className="fa-regular fa-ellipsis" />
      </Button>
      <Tooltip anchorElement="target" position="left">
        <Popup anchor={anchor.current} show={open} ref={popup}>
          <div className="qmb-popup--action-list">
            <ul role="presentation">
              {actionsList.map((action) => (
                <li key={action.title}>
                  {action.link && (
                    <a
                      onClick={() => setOpen(false)}
                      title={action.tooltip}
                      disabled={action.disabled}
                      href={action.link}
                      target={action.blank ? '_blank' : '_self'}
                      rel="noreferrer">
                      <i className={`fa-light ${action.faClass}`} />
                      {action.title}
                    </a>
                  )}
                  {action.onClick && (
                    <Button
                      disabled={action.disabled}
                      onClick={(e) => {
                        setOpen(false);
                        action.onClick(e);
                      }}
                      title={action.tooltip}>
                      <i className={`fa-light ${action.faClass}`} />
                      {action.title}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </Popup>
      </Tooltip>
    </td>
  );
}

ActionsCell.propTypes = {
  actionsList: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      link: PropTypes.string,
      faClass: PropTypes.string.isRequired,
      onClick: PropTypes.func,
      disabled: PropTypes.bool,
      tooltip: PropTypes.string,
      blank: PropTypes.bool
    })
  ).isRequired,
  colSpan: PropTypes.number,
  children: PropTypes.node,
  className: PropTypes.string,
  ariaColumnIndex: PropTypes.number,
  style: PropTypes.object
};

ActionsCell.defaultProps = {
  className: '',
  ariaColumnIndex: undefined,
  style: undefined,
  children: undefined,
  colSpan: undefined
};

export default ActionsCell;
